import React, { useEffect, useRef } from 'react';
import { PLAN_OPTIONS, WINDOW_OPTIONS, todayIso } from '../../utils/featureUsage';

/**
 * Filters panel for the Top Features page. Opened from the "Filters" button.
 * Controlled component: the parent owns the filter values.
 */
export default function FeatureUsageFilters({ open, filters, facets, localeLabel, onChange, onReset, onClose }) {
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const pluginVersions = facets?.plugin_version || [];
    const wpVersions = facets?.wp_version || [];
    const locales = facets?.locale || [];

    return (
        <div className="tf-filter-panel" id="tf-filter-panel" ref={panelRef} role="region" aria-label="Filters">
            <div className="tf-filter-field">
                <span className="tf-filter-label" id="tf-plan-label">Plan</span>
                <div className="tf-segmented" role="group" aria-labelledby="tf-plan-label">
                    {PLAN_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={filters.plan === option.value}
                            className={filters.plan === option.value ? 'is-active' : ''}
                            onClick={() => onChange('plan', option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <label className="tf-filter-field">
                <span className="tf-filter-label">Plugin version</span>
                <select value={filters.plugin_version} onChange={(e) => onChange('plugin_version', e.target.value)}>
                    <option value="">All versions</option>
                    {pluginVersions.map((v) => (
                        <option key={v.value} value={v.value}>{`${v.value}.x`}</option>
                    ))}
                </select>
            </label>

            <label className="tf-filter-field">
                <span className="tf-filter-label">WordPress version</span>
                <select value={filters.wp_version} onChange={(e) => onChange('wp_version', e.target.value)}>
                    <option value="">All versions</option>
                    {wpVersions.map((v) => (
                        <option key={v.value} value={v.value}>{v.value}</option>
                    ))}
                </select>
            </label>

            <label className="tf-filter-field">
                <span className="tf-filter-label">Language</span>
                <select value={filters.locale} onChange={(e) => onChange('locale', e.target.value)}>
                    <option value="">All languages</option>
                    {locales.map((v) => (
                        <option key={v.value} value={v.value}>{localeLabel(v.value)}</option>
                    ))}
                </select>
            </label>

            <label className="tf-filter-field">
                <span className="tf-filter-label">Reported in</span>
                <select
                    value={filters.window}
                    onChange={(e) => onChange('window', e.target.value === 'custom' ? 'custom' : Number(e.target.value))}
                >
                    {WINDOW_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </label>

            {filters.window === 'custom' && (
                <div className="tf-filter-field tf-date-field">
                    <span className="tf-filter-label" id="tf-range-label">Date range</span>
                    <div className="tf-date-range" role="group" aria-labelledby="tf-range-label">
                        <label className="tf-date-input">
                            <span>From</span>
                            <input
                                type="date"
                                value={filters.since}
                                max={todayIso()}
                                onChange={(e) => onChange('since', e.target.value)}
                                aria-label="From date"
                            />
                        </label>
                        <label className="tf-date-input">
                            <span>To</span>
                            <input type="date" value={todayIso()} disabled aria-label="To date (today)" />
                        </label>
                    </div>
                    <span className="tf-filter-hint">
                        {filters.since ? 'Sites that reported from this date until today.' : 'Pick a start date.'}
                    </span>
                </div>
            )}

            <div className="tf-filter-field">
                <span className="tf-filter-label">Sites</span>
                <label className="telemetry-checkbox-label tf-checkbox">
                    <input
                        type="checkbox"
                        checked={filters.only_production}
                        onChange={(e) => onChange('only_production', e.target.checked)}
                    />
                    Live sites only
                </label>
            </div>

            <div className="tf-filter-actions">
                <button type="button" className="tf-link-button" onClick={onReset}>Reset filters</button>
                <button type="button" className="tf-primary-button" onClick={onClose}>Done</button>
            </div>
        </div>
    );
}
